import {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Card,
    Text,
    Stack,
    Group,
    Collapse,
    ActionIcon,
    Tooltip,
    Alert,
} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import IconMail from "../../../../assets/mail.svg?react";
import IconCopy from "../../../../assets/copy.svg?react";
import IconCheck from "../../../../assets/check.svg?react";
import IconExternalLink from "../../../../assets/external-link.svg?react";
import IconChevronDown from "../../../../assets/chevron-down.svg?react";
import {Artifact} from "../../../../types";
import {useAnimatedText} from "../../hooks";
import {getArtifactTitle} from "./title";
import classes from "./EmailDraftRenderer.module.css";
import {EmailPayload, emailPlainText} from "./emailPayload";
import {useInteractionAnalytics} from "../../useInteractionAnalytics";

interface EmailDraftRendererProps {
    artifact: Artifact;
}

const MAX_MAILTO_LENGTH = 2000;

export function EmailDraftRenderer({artifact}: EmailDraftRendererProps) {
    const {t} = useTranslation();
    const [opened, {toggle}] = useDisclosure(true);
    const [showLengthWarning, setShowLengthWarning] = useState(false);
    const [copied, setCopied] = useState(false);
    const analytics = useInteractionAnalytics();
    const payload = artifact.payload as unknown as EmailPayload;
    const animatedBody = useAnimatedText(payload?.body ?? "");

    if (!payload?.subject && !payload?.body) {
        return (
            <Card withBorder p="md" radius="md">
                <Text size="sm" c="dimmed">
                    {t("artifact.emailDraft.empty")}
                </Text>
            </Card>
        );
    }

    const plainText = emailPlainText(payload, t("artifact.emailDraft.subjectLabel"));

    const mailtoBody = [payload.greeting, "", payload.body, "", payload.signOff]
        .filter(Boolean)
        .join("\r\n");
    const fullMailtoUrl = `mailto:?subject=${encodeURIComponent(payload.subject || "")}&body=${encodeURIComponent(mailtoBody)}`;
    const bodyTooLong = fullMailtoUrl.length > MAX_MAILTO_LENGTH;
    const mailtoUrl = bodyTooLong
        ? `mailto:?subject=${encodeURIComponent(payload.subject || "")}`
        : fullMailtoUrl;

    const handleMailtoClick = () => {
        // A mailto activation asks the browser to hand off; it cannot confirm a
        // mail client opened or that an email was sent.
        analytics.emailHandoffRequested(artifact.id, !bodyTooLong);
        if (bodyTooLong) {
            setShowLengthWarning(true);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(plainText);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2_000);
            analytics.emailCopied(artifact.id);
        } catch {
            // A clipboard failure is not a successful copy and remains invisible
            // to analytics as well as to the rest of the email-draft flow.
        }
    };

    return (
        <Card withBorder radius="md" p={0} className={classes.card}>
            <Group gap="xs" className={classes.header} justify="space-between">
                <Group
                    gap="xs"
                    wrap="nowrap"
                    className={classes.headerTitleGroup}
                    onClick={toggle}
                >
                    <IconChevronDown
                        width={14}
                        height={14}
                        className={`${classes.headerIcon} ${classes.collapseChevron} ${opened ? "" : classes.collapseChevronClosed}`}
                    />
                    <IconMail
                        width={16}
                        height={16}
                        className={classes.headerIcon}
                    />
                    <Text size="sm" fw={600} c="dibblue.9" className={classes.headerTitle}>
                        {getArtifactTitle(artifact)}
                    </Text>
                </Group>
                <Group gap={4}>
                    <Tooltip
                        label={copied ? t("artifact.emailDraft.copied") : t("artifact.emailDraft.copyEmail")}
                        withArrow
                        position="top"
                    >
                        <ActionIcon
                            variant="subtle"
                            color={copied ? "teal" : "gray"}
                            onClick={() => void handleCopy()}
                            size="sm"
                        >
                            {copied ? (
                                <IconCheck width={14} height={14} />
                            ) : (
                                <IconCopy width={14} height={14} />
                            )}
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip
                        label={t("artifact.emailDraft.openInClient")}
                        withArrow
                        position="top"
                    >
                        <ActionIcon
                            component="a"
                            href={mailtoUrl}
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={handleMailtoClick}
                        >
                            <IconExternalLink width={14} height={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Group>

            {showLengthWarning && (
                <Alert
                    variant="light"
                    color="dibblue"
                    withCloseButton
                    onClose={() => setShowLengthWarning(false)}
                    py="xs"
                    px="lg"
                    mt="xs"
                    mx="sm"
                >
                    <Text size="xs">
                        {t("artifact.emailDraft.tooLongWarning")}
                    </Text>
                </Alert>
            )}

            <Collapse in={opened}>
                <Stack gap="sm" className={classes.body}>
                    {payload.greeting && (
                        <Text size="sm" className={classes.bodyText}>
                            {payload.greeting}
                        </Text>
                    )}
                    {animatedBody && (
                        <Text size="sm" className={classes.bodyText}>
                            {animatedBody}
                        </Text>
                    )}
                    {payload.signOff && (
                        <Text size="sm" className={classes.bodyText}>
                            {payload.signOff}
                        </Text>
                    )}
                </Stack>
            </Collapse>
        </Card>
    );
}
